// # assets
import dotsBottom from "../../assets/login-dots-bottom.svg";
import dotsTop from "../../assets/login-dots-top.svg";
import vectorArt from "../../assets/login-vector-art.png";
import logo from "../../assets/top-nav-logo.svg";

// # css
import "./loginStyles.css";
import colors from "../design/colors";

// # lib
import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { loginValidation } from "../../request-handlers/validation/request-validation";
import {
  saveAccountDetails,
  saveSelectedOrg,
  setAllOrgsDetails,
} from "../features/account/account";
import { login, setRedirectOnAuth } from "../features/user/user";
import { loginAttempt } from "../features/user/user";

// # components
import Button from "../design/Button";
import { JService } from "../api-service/ApiService";
import { enqueueSnackbar } from "notistack";
import { reduxPersistSelectedVirtualNumber } from "../features/virtual-number/virtualNumber";

const localStyles = {
  inputField: {
    border: `1px solid ${colors.nc_gray_300}`,
    color: colors.nc_gray_900,
    height: "36px",
  },
  screenOverlay: {
    backgroundColor: "#ffffff77",
    position: "fixed",
    top: 0,
    left: 0,
    zIndex: 1302,
    height: window.innerHeight,
    width: window.innerWidth,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  loggingInText: {
    color: colors.ic_green_500,
    display: "inline-block",
    marginTop: "8px",
  },
};


const API_BASE_URL = process.env.REACT_APP_BETA;
export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reduxAccountRef = useRef(null);

  const userLoginAttemptWith = useSelector((state) => state.user.value);
  const appState = useSelector((state) => state?.app?.value);

  const [isSubmitting, setIsSubmitting] = useState(true);
  const [loginValues, setLoginValues] = useState({
    email: "",
    password: "",
  });

  
  const handleOnboardingNavigation = (step) => {
    if (step === "EMAIL_VERIFICATION") {
      dispatch(loginAttempt({email: loginValues.email, password: loginValues.password}));
      navigate("/signup/verification");
    }else if (step === "COMPANY_DETAILS") {
      navigate("/signup/company-details");
    }else if (step === "INITIATE_TRIAL") {
      navigate("/signup/account-type");
    }else if (step === "KYC" || step === "AGREEMENT") {
      navigate("/signup/kyc");
    }else {
      return null;
    }
  }

  const handleLoginChange = (e) => {
    const name = e.target.name;
    let value = e.target.value;
    if (name === "email") value = value.toLowerCase();
    setLoginValues((prev) => ({ ...prev, [name]: value }));
  };

  // autologin when user lands on the page
  useEffect(() => {
    JService.get("/api/v1/auth/retrieve-account-profile/")
      .then((res) => {
        if (!res?.success) {
          dispatch(saveAccountDetails({}));
          throw new Error(
            res?.message || "Invalid or empty authentication details provided"
          );
        }
        if(res?.next_onboarding_step) {
          handleOnboardingNavigation(res?.next_onboarding_step);
          return;
        }
        // dispatch(setRedirectOnAuth(true));
        if (appState === "WhatsApp Business") {
          navigate("/w-chat", { replace: true });
        } else if (appState === "Business phone") {
          dispatch(setRedirectOnAuth(true));
        }
      })
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        resetStates();
      });
  }, []);

  const handleLoginSubmit = (e) => {
    e.preventDefault();
    const { isValid } = loginValidation(loginValues);
    if (isValid) {
      // saving login attempt values
      dispatch(loginAttempt(loginValues));

      if (!isSubmitting) setIsSubmitting(true);

      fetch(API_BASE_URL + "/api/v1/auth/login/?source=web&webapp=true", {
        method: "post",
        body: JSON.stringify(loginValues),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((res) => res?.json())
        .then((res) => {
          if (!res?.success)
            throw new Error(
              Array.isArray(res?.message)
                ? (res?.message[0] ?? "Failed to login")
                : (res?.message ?? "Failed to login")
            );
          const searchParams = new URLSearchParams(window.location.search);
          const supportParam = searchParams.get("support")
            ? "/?support=true"
            : "";
          if (res?.two_factor_auth_enabled)
            navigate(`/login/verification${supportParam}`, {
              state: {
                provisoningUrl: res?.provisioning_uri,
                isNewlyCreated: res?.is_newly_created,
                method: res?.two_factor_auth_type,
              },
            });
          else {
            // saving login attempt details
            dispatch(login(userLoginAttemptWith));

            if(res?.next_onboarding_step) {
              handleOnboardingNavigation(res?.next_onboarding_step);
              return;
            }
            JService.get("/api/v1/auth/retrieve-account-profile/")
              .then((_res) => {
                if (!_res?.success)
                  throw new Error(
                    _res?.message || "Failed to fetch account details"
                  );

                // saving account details
                dispatch(saveAccountDetails(_res));
                if (_res?.data?.profile?.primary_virtual_number) {
                  dispatch(
                    reduxPersistSelectedVirtualNumber(
                      _res?.data?.profile?.primary_virtual_number
                    )
                  );
                }
                reduxAccountRef.current = _res;

                // account holder can add multiple accounts, and some apis
                // which are account/org specific like retrieve-call-logs/orgId,
                // retrieve-org/orgId etc. need the orgId suffix
                if (_res?.data?.profile?.account_holder) {
                  JService.get("/api/v1/auth/organizations/")
                    .then((__res) => {
                      if (!__res?.success)
                        throw new Error(
                          __res?.message || "Could not retrieve organizations"
                        );

                      dispatch(setAllOrgsDetails(__res?.data));
                      dispatch(
                        saveSelectedOrg(
                          __res?.data?.find((rd) => rd?.primary_account)?.id ||
                            ""
                        )
                      );
                      dispatch(setRedirectOnAuth(true));
                    })
                    .catch((__err) =>
                      enqueueSnackbar(__err?.message, { variant: "error" })
                    );
                } 
                else {
                  // for users accessing the shared-call recording link
                  dispatch(setRedirectOnAuth(true));
                }
              })
              .catch((_err) =>
                enqueueSnackbar(_err?.message, { variant: "error" })
              )
              .finally(() => (isSubmitting ? setIsSubmitting(false) : null));
          }
        })
        .catch((err) => enqueueSnackbar(err?.message, { variant: "error" }))
        .finally(() => {
          resetStates();
        });
    }
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    navigate("/forgot-password");
  };

  const resetStates = (arg = "password") => {
    setLoginValues((prev) => ({ ...prev, [arg]: "" }));
    setIsSubmitting(false);
  };

  return (
    <>
      <div className="login-container">
        <div className="login-art">
          <a
            href="https://frejun.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {" "}
            <img src={logo} alt="Logo" />
          </a>
          <img src={dotsTop} alt=""/>
          <img src={vectorArt} alt=""/>
          <img src={dotsBottom} alt=""/>
          <p className="regular-font t5">
            Simplify and Automate
            <br />
            Your Calling Process
          </p>
        </div>
        <h1 style={{ position: "absolute", visibility: "hidden" }}>
          Frejun Login
        </h1>
        <div className="login-form">
          <p className="title medium-font t5">Log in</p>
          <form id="login-form-form" onSubmit={handleLoginSubmit}>
            <label className="medium-font t7">
              Email
              <input
                className="regular-font t6 ui--input ui--input-email"
                style={{ ...localStyles.inputField }}
                name="email"
                type="text"
                value={loginValues?.email}
                onChange={(e) => handleLoginChange(e)}
              />
            </label>

            <label className="medium-font t7">
              Password
              <input
                className="regular-font t6 ui--input ui--input-password"
                style={{ ...localStyles.inputField, marginBottom: '16px' }}
                name="password"
                type="password"
                value={loginValues?.password}
                onChange={(e) => handleLoginChange(e)}
              />
            </label>

            <label className="to-forgot-password-container">
              <a
                href="/forgot-password"
                className="to-forgot-password regular-font t7 ui--forgot-password to-sign-up"
                onClick={(e) => handleForgotPasswordClick(e)}
              >
                Forgot Password
              </a>
            </label>

            <Button
              className="ui--login ui--button"
              form="login-form-form"
              type="submit"
              disabled={!loginValidation(loginValues).isValid}
              hierarchy="green"
              variant="contained"
              buttonText="Log in"
              isLoading={isSubmitting}
              onClick={handleLoginSubmit}
              onSubmit={handleLoginSubmit}
              styleOverride={{ left: "50%", transform: "translate(-50%)", width: '100%' }}
            />
          </form>
          <div className="regular-font t7 to-sign-up-container">
            Don't have an account?
            <a
              href="/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="to-sign-up"
            >
              Sign up
            </a>
          </div>
        </div>
      </div>
    </>
  );
}
